/**
 * WEG-Verwaltung — Phase 1 Fundament
 *
 * Listet alle Objekte mit object_type = 'weg' oder 'mixed'. Kennzahlen:
 *   - Einheiten (Sondereigentum-Anzahl)
 *   - MEA-Summe / MEA-Nenner (mit Warnung wenn ≠)
 *   - Eigentümer-Anzahl
 *   - Beirats-Größe
 *
 * Placeholder-Kacheln für kommende Phasen: Beschluss-Sammlung, Wirtschaftsplan,
 * Hausgeld, Versammlung, Jahresabrechnung.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Buildings, Warning, CheckCircle, Users, Scales, Info } from "@phosphor-icons/react";
import { WegHubTabs } from "@/components/weg/WegHub";

const PLACEHOLDERS = [];

export default function WEG() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/weg/overview").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="weg-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Buildings weight="duotone" className="text-emerald-700" /> WEG-Verwaltung
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Wohnungseigentümergemeinschaften — Beschlusssammlung, Eigentümerversammlungen mit Präsentationsmodus, Miteigentumsanteile, Wirtschaftsplan, Hausgeld und Jahresabrechnung.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild data-testid="weg-goto-konten">
            <Link to="/weg/konten">Kontenrahmen</Link>
          </Button>
        </div>
      </div>

      <WegHubTabs />

      <section className="space-y-3">
        <h2 className="text-base font-medium text-slate-700">WEG-Objekte im Bestand</h2>
        {loading ? (
          <div className="text-sm text-slate-500">Lade …</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center bg-slate-50" data-testid="weg-empty-state">
            <Buildings size={40} className="mx-auto text-slate-400 mb-3" />
            <div className="text-sm text-slate-600">
              Noch keine Objekte als WEG markiert.
              <br />
              Öffne ein Objekt und ändere den <strong>Objekt-Typ</strong> auf <em>WEG</em> oder <em>Mixed</em>.
            </div>
            <Button asChild variant="outline" size="sm" className="mt-4">
              <Link to="/objekte" data-testid="weg-goto-properties">Zu den Objekten</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map((r) => (
              <Link
                key={r.id}
                to={`/objekte/${r.id}?tab=weg`}
                data-testid={`weg-card-${r.property_number}`}
                className="block rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-emerald-400 transition"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs font-mono text-slate-500">{r.property_number}</div>
                    <div className="text-base font-semibold text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">
                      {r.address?.street} {r.address?.house_number}, {r.address?.zip} {r.address?.city}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                      r.object_type === "weg" ? "bg-emerald-100 text-emerald-800"
                      : r.object_type === "seg" ? "bg-purple-100 text-purple-800"
                      : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {r.object_type === "weg" ? "WEG" : r.object_type === "seg" ? "SEG" : "Mixed"}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-[11px] text-slate-500">Einheiten</div>
                    <div className="font-medium text-slate-800">{r.unit_count}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Scales size={12} /> MEA
                    </div>
                    <div className={`font-medium ${r.mea_complete ? "text-emerald-700" : "text-amber-700"}`}>
                      {r.mea_sum} / {r.mea_total}
                      {r.mea_complete ? (
                        <CheckCircle size={14} className="inline ml-1 -mt-0.5" weight="fill" />
                      ) : (
                        <Warning size={14} className="inline ml-1 -mt-0.5" weight="fill" />
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Users size={12} /> Eigent.
                    </div>
                    <div className="font-medium text-slate-800">{r.owner_count}</div>
                  </div>
                </div>

                {r.beirat_count > 0 && (
                  <div className="mt-3 text-xs text-slate-500">
                    Verwaltungsbeirat: {r.beirat_count} Mitglied{r.beirat_count === 1 ? "" : "er"}
                  </div>
                )}
                {r.resolution_count > 0 && (
                  <div className="mt-1 text-xs text-slate-500">
                    Beschlüsse: <span className="font-medium">{r.resolution_count}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {PLACEHOLDERS.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-base font-medium text-slate-700">Kommende WEG-Module</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            {PLACEHOLDERS.map((p) => (
              <div
                key={p.key}
                data-testid={`weg-placeholder-${p.key}`}
                className="rounded-lg border border-slate-200 bg-slate-50 p-4 opacity-70"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Info size={16} className="text-slate-400" />
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{p.phase}</span>
                </div>
                <div className="text-sm font-medium text-slate-800">{p.label}</div>
                <div className="text-xs text-slate-500 mt-1">{p.desc}</div>
              </div>
            ))}
          </div>
        </section>
      )}
      {PLACEHOLDERS.length === 0 && rows.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <CheckCircle size={14} weight="fill" className="inline mr-1.5" />
          Alle 6 WEG-Phasen sind implementiert: MEA/Beirat · Beschluss-Sammlung · Wirtschaftsplan · Hausgeld · Eigentümerversammlung · Jahresabrechnung.
        </div>
      )}
    </div>
  );
}
